import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, X, Sparkles, Search, Wand2, Scroll, Package, Clock, MapPin, Star } from 'lucide-react';
import { useInventory } from '../hooks/useInventory';
interface PresentationModeProps {
  onClose: () => void;
}
export function PresentationMode({
  onClose
}: PresentationModeProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const {
    inventory,
    getStats
  } = useInventory();
  const stats = getStats;
  const totalSlides = 7;
  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % totalSlides);
  }, []);
  const prevSlide = useCallback(() => {
    setCurrentSlide(prev => (prev - 1 + totalSlides) % totalSlides);
  }, []);
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        nextSlide();
      } else if (e.key === 'ArrowLeft') {
        prevSlide();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide, onClose]);
  // Categories from inventory
  const categories = [...new Set(inventory.map(item => item.category))];
  const sampleItems = inventory.slice(0, 6);
  return <div className="fixed inset-0 z-[100] bg-[#1a1a2e] overflow-hidden" data-id="element-319">
      {/* Magical background */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-[#722F37]/20 via-[#1a1a2e] to-[#1a1a2e]" data-id="element-320" />
      <div className="absolute inset-0 opacity-30" data-id="element-321">
        {[...Array(50)].map((_, i) => <div key={i} className="absolute w-1 h-1 bg-[#C9A227] rounded-full animate-pulse" style={{
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        animationDelay: `${Math.random() * 3}s`,
        animationDuration: `${2 + Math.random() * 2}s`
      }} data-id="element-322" />)}
      </div>

      {/* Close button */}
      <button onClick={onClose} className="absolute top-6 right-6 z-50 p-3 bg-[#722F37]/50 hover:bg-[#722F37] rounded-full text-[#F5F0E1] transition-colors border border-[#C9A227]/30" data-id="element-323">
        <X className="w-6 h-6" data-id="element-324" />
      </button>

      {/* Slide counter */}
      <div className="absolute top-6 left-6 z-50 text-[#C9A227] font-cinzel text-sm" data-id="element-325">
        {currentSlide + 1} / {totalSlides}
      </div>

      {/* Main slide content */}
      <div className="relative h-full flex items-center justify-center p-8" data-id="element-326">
        <div className="w-full max-w-5xl mx-auto" data-id="element-327">
          {/* Slide 1: Title */}
          {currentSlide === 0 && <div className="text-center animate-fade-in" data-id="element-328">
              <div className="mb-8" data-id="element-329">
                <Sparkles className="w-20 h-20 mx-auto text-[#C9A227] mb-6 animate-pulse" data-id="element-330" />
              </div>
              <h1 className="text-6xl md:text-8xl font-cinzel font-bold text-[#F5F0E1] mb-6 tracking-tight" data-id="element-331">
                The Household
                <span className="block text-[#C9A227]" data-id="element-332">Ledger</span>
              </h1>
              <p className="text-2xl text-[#F5F0E1]/70 font-serif italic mb-8" data-id="element-333">
                A Magical Inventory Management System
              </p>
              <div className="flex items-center justify-center gap-4 text-[#C9A227]/60 text-lg" data-id="element-334">
                <span data-id="element-335">⚡</span>
                <span className="font-serif" data-id="element-336">Enchanted in Alaska</span>
                <span data-id="element-337">⚡</span>
              </div>
              <div className="mt-16 text-[#F5F0E1]/40 text-sm font-serif" data-id="element-338">
                Press → or Space to continue
              </div>
            </div>}

          {/* Slide 2: The Problem */}
          {currentSlide === 1 && <div className="animate-fade-in" data-id="element-339">
              <h2 className="text-5xl font-cinzel font-bold text-[#C9A227] mb-12 text-center" data-id="element-340">
                The Challenge
              </h2>
              <div className="grid md:grid-cols-2 gap-8" data-id="element-341">
                <div className="bg-[#722F37]/30 border border-[#722F37] rounded-2xl p-8" data-id="element-342">
                  <h3 className="text-2xl font-cinzel text-[#F5F0E1] mb-4" data-id="element-343">
                    Without Magic
                  </h3>
                  <ul className="space-y-4 text-[#F5F0E1]/70 font-serif" data-id="element-344">
                    <li className="flex items-start gap-3" data-id="element-345">
                      <span className="text-[#8B0000]" data-id="element-346">✗</span>
                      <span data-id="element-347">
                        Forgotten items spoiling in the back of the pantry
                      </span>
                    </li>
                    <li className="flex items-start gap-3" data-id="element-348">
                      <span className="text-[#8B0000]" data-id="element-349">✗</span>
                      <span data-id="element-350">Duplicate purchases of items already in stock</span>
                    </li>
                    <li className="flex items-start gap-3" data-id="element-351">
                      <span className="text-[#8B0000]" data-id="element-352">✗</span>
                      <span data-id="element-353">No visibility into what's running low</span>
                    </li>
                    <li className="flex items-start gap-3" data-id="element-354">
                      <span className="text-[#8B0000]" data-id="element-355">✗</span>
                      <span data-id="element-356">Chaotic shopping trips without lists</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-[#2D5A3D]/30 border border-[#2D5A3D] rounded-2xl p-8" data-id="element-357">
                  <h3 className="text-2xl font-cinzel text-[#F5F0E1] mb-4" data-id="element-358">
                    With The Ledger
                  </h3>
                  <ul className="space-y-4 text-[#F5F0E1]/70 font-serif" data-id="element-359">
                    <li className="flex items-start gap-3" data-id="element-360">
                      <span className="text-[#2D5A3D]" data-id="element-361">✓</span>
                      <span data-id="element-362">Tempus warnings before items expire</span>
                    </li>
                    <li className="flex items-start gap-3" data-id="element-363">
                      <span className="text-[#2D5A3D]" data-id="element-364">✓</span>
                      <span data-id="element-365">Accio search finds anything instantly</span>
                    </li>
                    <li className="flex items-start gap-3" data-id="element-366">
                      <span className="text-[#2D5A3D]" data-id="element-367">✓</span>
                      <span data-id="element-368">Smart stock level tracking with alerts</span>
                    </li>
                    <li className="flex items-start gap-3" data-id="element-369">
                      <span className="text-[#2D5A3D]" data-id="element-370">✓</span>
                      <span data-id="element-371">Auto-generated Requisition Scrolls</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>}

          {/* Slide 3: Key Features */}
          {currentSlide === 2 && <div className="animate-fade-in" data-id="element-372">
              <h2 className="text-5xl font-cinzel font-bold text-[#C9A227] mb-12 text-center" data-id="element-373">
                Magical Spells
              </h2>
              <div className="grid md:grid-cols-3 gap-6" data-id="element-374">
                <div className="bg-[#F5F0E1]/10 border border-[#C9A227]/40 rounded-2xl p-8 text-center group hover:border-[#C9A227] transition-all" data-id="element-375">
                  <div className="w-16 h-16 mx-auto mb-4 bg-[#722F37] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-376">
                    <Search className="w-8 h-8 text-[#C9A227]" data-id="element-377" />
                  </div>
                  <h3 className="text-3xl font-cinzel font-bold text-[#F5F0E1] mb-2" data-id="element-378">
                    Accio
                  </h3>
                  <p className="text-[#F5F0E1]/60 font-serif" data-id="element-379">
                    Summon any item instantly with powerful search magic
                  </p>
                </div>
                <div className="bg-[#F5F0E1]/10 border border-[#C9A227]/40 rounded-2xl p-8 text-center group hover:border-[#C9A227] transition-all" data-id="element-380">
                  <div className="w-16 h-16 mx-auto mb-4 bg-[#722F37] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-381">
                    <Sparkles className="w-8 h-8 text-[#C9A227]" data-id="element-382" />
                  </div>
                  <h3 className="text-3xl font-cinzel font-bold text-[#F5F0E1] mb-2" data-id="element-383">
                    Revelio
                  </h3>
                  <p className="text-[#F5F0E1]/60 font-serif" data-id="element-384">
                    Reveal contents of any storage zone at a glance
                  </p>
                </div>
                <div className="bg-[#F5F0E1]/10 border border-[#C9A227]/40 rounded-2xl p-8 text-center group hover:border-[#C9A227] transition-all" data-id="element-385">
                  <div className="w-16 h-16 mx-auto mb-4 bg-[#722F37] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-386">
                    <Wand2 className="w-8 h-8 text-[#C9A227]" data-id="element-387" />
                  </div>
                  <h3 className="text-3xl font-cinzel font-bold text-[#F5F0E1] mb-2" data-id="element-388">
                    Conjure
                  </h3>
                  <p className="text-[#F5F0E1]/60 font-serif" data-id="element-389">
                    Add new items with a wave of your wand
                  </p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6 mt-6" data-id="element-390">
                <div className="bg-[#F5F0E1]/10 border border-[#C9A227]/40 rounded-2xl p-6 flex items-center gap-6 group hover:border-[#C9A227] transition-all" data-id="element-391">
                  <div className="w-14 h-14 bg-[#722F37] rounded-full flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform" data-id="element-392">
                    <Scroll className="w-7 h-7 text-[#C9A227]" data-id="element-393" />
                  </div>
                  <div data-id="element-394">
                    <h3 className="text-2xl font-cinzel font-bold text-[#F5F0E1]" data-id="element-395">
                      Requisition Scroll
                    </h3>
                    <p className="text-[#F5F0E1]/60 font-serif" data-id="element-396">
                      Auto-generated shopping lists with priorities
                    </p>
                  </div>
                </div>
                <div className="bg-[#F5F0E1]/10 border border-[#C9A227]/40 rounded-2xl p-6 flex items-center gap-6 group hover:border-[#C9A227] transition-all" data-id="element-397">
                  <div className="w-14 h-14 bg-[#722F37] rounded-full flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform" data-id="element-398">
                    <Clock className="w-7 h-7 text-[#C9A227]" data-id="element-399" />
                  </div>
                  <div data-id="element-400">
                    <h3 className="text-2xl font-cinzel font-bold text-[#F5F0E1]" data-id="element-401">
                      Tempus Warnings
                    </h3>
                    <p className="text-[#F5F0E1]/60 font-serif" data-id="element-402">
                      Never let items expire with time-based alerts
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {/* Slide 4: Live Stats */}
          {currentSlide === 3 && <div className="animate-fade-in" data-id="element-403">
              <h2 className="text-5xl font-cinzel font-bold text-[#C9A227] mb-4 text-center" data-id="element-404">
                Current Inventory
              </h2>
              <p className="text-center text-[#F5F0E1]/60 font-serif italic mb-12" data-id="element-405">
                Live data from The Household Ledger
              </p>
              <div className="grid md:grid-cols-4 gap-6 mb-12" data-id="element-406">
                <div className="bg-[#F5F0E1] rounded-2xl p-8 text-center border-2 border-[#C9A227]" data-id="element-407">
                  <div className="text-6xl font-cinzel font-bold text-[#722F37] mb-2" data-id="element-408">
                    {stats.totalItems}
                  </div>
                  <div className="text-sm uppercase tracking-widest text-[#722F37]/70" data-id="element-409">
                    Total Items
                  </div>
                </div>
                <div className="bg-[#F5F0E1] rounded-2xl p-8 text-center border-2 border-[#C9A227]" data-id="element-410">
                  <div className="text-6xl font-cinzel font-bold text-[#2D5A3D] mb-2" data-id="element-411">
                    {categories.length}
                  </div>
                  <div className="text-sm uppercase tracking-widest text-[#722F37]/70" data-id="element-412">
                    Categories
                  </div>
                </div>
                <div className="bg-[#F5F0E1] rounded-2xl p-8 text-center border-2 border-[#D4A017]" data-id="element-413">
                  <div className="text-6xl font-cinzel font-bold text-[#D4A017] mb-2" data-id="element-414">
                    {stats.lowStock}
                  </div>
                  <div className="text-sm uppercase tracking-widest text-[#722F37]/70" data-id="element-415">
                    Depleting
                  </div>
                </div>
                <div className="bg-[#F5F0E1] rounded-2xl p-8 text-center border-2 border-[#8B0000]" data-id="element-416">
                  <div className="text-6xl font-cinzel font-bold text-[#8B0000] mb-2" data-id="element-417">
                    {stats.expiringSoon}
                  </div>
                  <div className="text-sm uppercase tracking-widest text-[#722F37]/70" data-id="element-418">
                    Tempus Alert
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap justify-center gap-3" data-id="element-419">
                {categories.map(cat => <span key={cat} className="px-4 py-2 bg-[#722F37]/30 border border-[#C9A227]/40 rounded-full text-[#F5F0E1] font-serif text-sm" data-id="element-420">
                    {cat}
                  </span>)}
              </div>
            </div>}

          {/* Slide 5: Sample Items */}
          {currentSlide === 4 && <div className="animate-fade-in" data-id="element-421">
              <h2 className="text-5xl font-cinzel font-bold text-[#C9A227] mb-4 text-center" data-id="element-422">
                Magical Provisions
              </h2>
              <p className="text-center text-[#F5F0E1]/60 font-serif italic mb-12" data-id="element-423">
                A glimpse into our enchanted inventory
              </p>
              <div className="grid md:grid-cols-3 gap-4" data-id="element-424">
                {sampleItems.map(item => <div key={item.id} className="bg-[#F5F0E1] rounded-xl p-4 border border-[#C9A227]" data-id="element-425">
                    <div className="flex items-center gap-3 mb-3" data-id="element-426">
                      <div className="w-12 h-12 bg-[#722F37]/10 rounded-lg flex items-center justify-center overflow-hidden" data-id="element-427">
                        {item.photoUrl ? <img src={item.photoUrl} alt={item.name} className="w-full h-full object-cover" data-id="element-428" /> : <Package className="w-6 h-6 text-[#722F37]" data-id="element-429" />}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-430">
                        <h4 className="font-cinzel font-bold text-[#2C2C2C] truncate" data-id="element-431">
                          {item.name}
                        </h4>
                        <p className="text-xs text-[#722F37] font-serif italic" data-id="element-432">
                          {item.category}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm" data-id="element-433">
                      <span className="text-[#555]" data-id="element-434">
                        {item.quantity} {item.unit}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${item.status === 'Critical' ? 'bg-[#8B0000]/20 text-[#8B0000]' : item.status === 'Warning' ? 'bg-[#D4A017]/20 text-[#D4A017]' : 'bg-[#2D5A3D]/20 text-[#2D5A3D]'}`} data-id="element-435">
                        {item.status}
                      </span>
                    </div>
                  </div>)}
              </div>
            </div>}

          {/* Slide 6: Storage Zones */}
          {currentSlide === 5 && <div className="animate-fade-in" data-id="element-436">
              <h2 className="text-5xl font-cinzel font-bold text-[#C9A227] mb-4 text-center" data-id="element-437">
                Storage Chambers
              </h2>
              <p className="text-center text-[#F5F0E1]/60 font-serif italic mb-12" data-id="element-438">
                Five enchanted zones to organize your provisions
              </p>
              <div className="grid md:grid-cols-5 gap-4" data-id="element-439">
                {[{
              name: 'Provisions',
              zone: 'Kitchen Pantry',
              icon: '🏺',
              color: '#722F37'
            }, {
              name: 'Cold Storage',
              zone: 'Refrigerator',
              icon: '❄️',
              color: '#2D5A3D'
            }, {
              name: 'Deep Freeze',
              zone: 'Freezer',
              icon: '🧊',
              color: '#1E3A8A'
            }, {
              name: 'Apothecary',
              zone: 'Bathroom',
              icon: '⚗️',
              color: '#C9A227'
            }, {
              name: 'Maintenance',
              zone: 'Cleaning Closet',
              icon: '🧹',
              color: '#4A4A4A'
            }].map(zone => <div key={zone.zone} className="bg-[#F5F0E1]/10 border border-[#C9A227]/40 rounded-2xl p-6 text-center" data-id="element-440">
                    <div className="text-4xl mb-3" data-id="element-441">{zone.icon}</div>
                    <h3 className="font-cinzel font-bold text-[#F5F0E1] mb-1" data-id="element-442">
                      {zone.name}
                    </h3>
                    <p className="text-xs text-[#F5F0E1]/50 font-serif" data-id="element-443">
                      {zone.zone}
                    </p>
                    <div className="mt-3 text-2xl font-cinzel font-bold" style={{
                color: zone.color
              }} data-id="element-444">
                      {inventory.filter(i => i.zone === zone.zone).length}
                    </div>
                    <p className="text-xs text-[#F5F0E1]/40" data-id="element-445">items</p>
                  </div>)}
              </div>
            </div>}

          {/* Slide 7: Thank You */}
          {currentSlide === 6 && <div className="text-center animate-fade-in" data-id="element-446">
              <Star className="w-16 h-16 mx-auto text-[#C9A227] mb-8 animate-pulse" data-id="element-447" />
              <h2 className="text-6xl font-cinzel font-bold text-[#F5F0E1] mb-6" data-id="element-448">
                Mischief Managed
              </h2>
              <p className="text-2xl text-[#F5F0E1]/70 font-serif italic mb-12" data-id="element-449">
                Thank you for your attention, witches and wizards!
              </p>
              <div className="bg-[#F5F0E1]/10 border border-[#C9A227] rounded-2xl p-8 max-w-md mx-auto" data-id="element-450">
                <p className="text-[#C9A227] font-cinzel text-xl mb-4" data-id="element-451">
                  Ready for a Demo?
                </p>
                <p className="text-[#F5F0E1]/60 font-serif" data-id="element-452">
                  Press{' '}
                  <kbd className="px-2 py-1 bg-[#722F37] rounded text-[#F5F0E1] text-sm mx-1" data-id="element-453">
                    ESC
                  </kbd>{' '}
                  to return to the app
                </p>
              </div>
              <div className="mt-12 flex items-center justify-center gap-8 text-[#F5F0E1]/40 font-serif" data-id="element-454">
                <span data-id="element-455">Built with React</span>
                <span data-id="element-456">•</span>
                <span data-id="element-457">Styled with Tailwind</span>
                <span data-id="element-458">•</span>
                <span data-id="element-459">Enchanted in Alaska</span>
              </div>
            </div>}
        </div>
      </div>

      {/* Navigation arrows */}
      <button onClick={prevSlide} className="absolute left-6 top-1/2 -translate-y-1/2 p-4 bg-[#722F37]/50 hover:bg-[#722F37] rounded-full text-[#F5F0E1] transition-colors border border-[#C9A227]/30" data-id="element-460">
        <ChevronLeft className="w-8 h-8" data-id="element-461" />
      </button>
      <button onClick={nextSlide} className="absolute right-6 top-1/2 -translate-y-1/2 p-4 bg-[#722F37]/50 hover:bg-[#722F37] rounded-full text-[#F5F0E1] transition-colors border border-[#C9A227]/30" data-id="element-462">
        <ChevronRight className="w-8 h-8" data-id="element-463" />
      </button>

      {/* Slide indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2" data-id="element-464">
        {[...Array(totalSlides)].map((_, i) => <button key={i} onClick={() => setCurrentSlide(i)} className={`w-3 h-3 rounded-full transition-all ${i === currentSlide ? 'bg-[#C9A227] w-8' : 'bg-[#F5F0E1]/30 hover:bg-[#F5F0E1]/50'}`} data-id="element-465" />)}
      </div>
    </div>;
}